// Permission + role catalog mirrored from
// backoffice-service/internal/domain/permissions.go. Keep these in
// sync with the Go constants (and the seed in 0009_seed_rbac.up.sql)
// — the Go side is the source of truth; this file is the typed
// frontend mirror that powers nav-item gating.

// --- Role names (built-in, from domain/permissions.go) ---
export const Role = {
  SuperAdmin: "SUPER_ADMIN",
  MarketOps:  "MARKET_OPS",
  Finance:    "FINANCE",
  Support:    "SUPPORT",
  ReadOnly:   "READONLY",
  // SM-301: authors private polls and requests publication. Holds no
  // publish/resolve/treasury permission — but may still receive
  // commission, which is independent of role.
  Creator:    "CREATOR",
} as const;
export type RoleName = (typeof Role)[keyof typeof Role];

// --- Permission codes (must match domain.PermXxx) ---
export const Perm = {
  // catalog
  CategoryCreate: "CATEGORY_CREATE",
  CategoryUpdate: "CATEGORY_UPDATE",
  CategoryDelete: "CATEGORY_DELETE",
  TopicCreate:    "TOPIC_CREATE",
  TopicUpdate:    "TOPIC_UPDATE",
  TopicDelete:    "TOPIC_DELETE",
  RuleCreate:     "RULE_CREATE",
  RuleUpdate:     "RULE_UPDATE",
  RuleOpen:       "RULE_OPEN",
  RuleClose:      "RULE_CLOSE",
  RuleCancel:     "RULE_CANCEL",
  RuleResolve:    "RULE_RESOLVE",
  RuleSettle:     "RULE_SETTLE",
  RuleSetSTP:     "RULE_SET_STP",

  // private poll authoring (SM-301 — backoffice 0024_seed_creator_commission_rbac).
  //
  // CatalogViewAll is a NEGATIVE gate for the UI: don't hide anything
  // when it's missing, because the server already scoped the response
  // to the admin's own rows. Use it only to decide whether to show
  // cross-admin affordances (creator/recipient filters, "all admins"
  // columns) that would render empty or misleading when scoped.
  CatalogViewAll:          "CATALOG_VIEW_ALL",
  TopicUpdateOwnDraft:     "TOPIC_UPDATE_OWN_DRAFT",
  RuleUpdateOwnDraft:      "RULE_UPDATE_OWN_DRAFT",
  PublicationRequestCreate: "PUBLICATION_REQUEST_CREATE",
  PublicationRequestReview: "PUBLICATION_REQUEST_REVIEW",

  // own-scoped removal (SM-359 — backoffice 0025_seed_creator_own_catalog_rbac).
  // The narrow siblings of RuleCancel / RuleUpdate / TopicDelete: they
  // authorise the action on rows you authored. Never gate a UI action on
  // the broad code alone, or an author sees a catalog they cannot manage
  // — which is the bug this ticket fixed. Use useCatalogAbilities().
  RuleCancelOwn:  "RULE_CANCEL_OWN",
  RuleDeleteOwn:  "RULE_DELETE_OWN",
  TopicDeleteOwn: "TOPIC_DELETE_OWN",

  // users
  UserView:              "USER_VIEW",
  UserViewPII:           "USER_VIEW_PII",
  UserSetStatus:         "USER_SET_STATUS",
  UserKYCReview:         "USER_KYC_REVIEW",
  UserAssignRole:        "USER_ASSIGN_ROLE",
  UserRevokeAllSessions: "USER_REVOKE_ALL_SESSIONS",

  // comments (per-market-group discussion moderation)
  CommentView:     "COMMENT_VIEW",
  CommentModerate: "COMMENT_MODERATE",

  // campaigns (promotion-service — promotional giveaways)
  CampaignView:      "CAMPAIGN_VIEW",
  CampaignCreate:    "CAMPAIGN_CREATE",
  CampaignUpdate:    "CAMPAIGN_UPDATE",
  CampaignDelete:    "CAMPAIGN_DELETE",
  CampaignBlacklist: "CAMPAIGN_BLACKLIST",

  // wallets / treasury
  WalletView:     "WALLET_VIEW",
  WalletFreeze:   "WALLET_FREEZE",
  WalletCredit:   "WALLET_CREDIT",
  DepositView:    "DEPOSIT_VIEW",
  WithdrawalView: "WITHDRAWAL_VIEW",

  // system accounts (SM-152 treasury — backoffice 0019_seed_system_accounts_rbac,
  // category "treasury"). SUPER_ADMIN has all; FINANCE gets the money
  // perms; SUPPORT/READONLY are view-only.
  SystemAccountView:            "SYSTEM_ACCOUNT_VIEW",
  SystemAccountCreate:          "SYSTEM_ACCOUNT_CREATE",
  SystemAccountUpdate:          "SYSTEM_ACCOUNT_UPDATE",
  SystemAccountTransfer:        "SYSTEM_ACCOUNT_TRANSFER",
  SystemAccountDeposit:         "SYSTEM_ACCOUNT_DEPOSIT",
  SystemAccountWithdraw:        "SYSTEM_ACCOUNT_WITHDRAW",
  SystemAccountAllocateRevenue: "SYSTEM_ACCOUNT_ALLOCATE_REVENUE",
  SystemAccountFundUser:        "SYSTEM_ACCOUNT_FUND_USER",

  // GREEN conversion operations (SM-421/SM-423 — backoffice 0030,
  // category "treasury"). Split three ways because the three powers are
  // genuinely different: reading pool liquidity, changing the rate a
  // user converts at, and moving money into a pool. FINANCE holds all
  // three; SUPPORT and READONLY get the view code only.
  ConversionView:       "CONVERSION_VIEW",
  ConversionConfig:     "CONVERSION_CONFIG",
  ConversionPoolManage: "CONVERSION_POOL_MANAGE",

  // Currency registry operator settings (SM-485). Same view-vs-mutate
  // split as the conversion triple above: seeing what a rail is set to
  // and changing it are different powers. CurrencyConfig can never
  // widen a capability CEILING — those are immutable in the ledger
  // schema and refused by name in both hops.
  CurrencyView:         "CURRENCY_VIEW",
  CurrencyConfig:       "CURRENCY_CONFIG",

  // admin creator commissions (SM-301 — backoffice 0024). None of these
  // gate /me/balance or self earnings: every admin sees their own money
  // without a permission, so never hide that nav item behind them.
  CommissionConfig:  "COMMISSION_CONFIG",
  CommissionView:    "COMMISSION_VIEW",
  AdminPayoutReview: "ADMIN_PAYOUT_REVIEW",

  // redemption fee (SM-327 — backoffice 0027). Split like commission:
  // auditing what the platform charges and changing it are different
  // powers. FINANCE holds both; MARKET_OPS, SUPPORT and READONLY hold
  // only the view code — the trading-fee pages still run on RuleUpdate,
  // which is a catalog permission and deliberately not this.
  FeeConfigView:   "FEE_CONFIG_VIEW",
  FeeConfigUpdate: "FEE_CONFIG_UPDATE",

  // disputes + admin alerts (SM-291 — backoffice 0023_disputes_and_alerts,
  // category "disputes"). MARKET_OPS reviews (it resolves the markets
  // that attract disputes); FINANCE and SUPPORT see them; the policy
  // (stake amount, window) stays SUPER_ADMIN.
  //
  // DisputeReview does double duty: it gates adjudication AND defines the
  // dispute-alert email audience, so granting it subscribes an admin.
  DisputeView:    "DISPUTE_VIEW",
  DisputeReview:  "DISPUTE_REVIEW",
  DisputePolicy:  "DISPUTE_POLICY",
  AlertView:      "ALERT_VIEW",
  AlertConfigure: "ALERT_CONFIGURE",

  // trading (read-only)
  OrderView:      "ORDER_VIEW",
  PositionView:   "POSITION_VIEW",
  OrderbookView:  "ORDERBOOK_VIEW",

  // engine ops
  EngineView:      "ENGINE_VIEW",
  EngineScale:     "ENGINE_SCALE",
  EngineRebalance: "ENGINE_REBALANCE",
  EnginePeerDrain: "ENGINE_PEER_DRAIN",

  // admin / RBAC
  AdminView:         "ADMIN_VIEW",
  AdminCreate:       "ADMIN_CREATE",
  AdminUpdate:       "ADMIN_UPDATE",
  AdminDelete:       "ADMIN_DELETE",
  AdminAssignRole:   "ADMIN_ASSIGN_ROLE",
  AdminViewAuditLog: "ADMIN_VIEW_AUDIT_LOG",
  RoleManage:        "ROLE_MANAGE",

  // system config
  SystemConfigView:   "SYSTEM_CONFIG_VIEW",
  SystemConfigUpdate: "SYSTEM_CONFIG_UPDATE",

  // market-maker money operations (backoffice 0028 / 0029). Enforced in
  // market-maker, which reads the permission header the gateway
  // forwards; listed here so the role editor can grant it. There is no
  // backoffice page gated on it — MM Web is where the action lives.
  MMAccountTransfer: "MM_ACCOUNT_TRANSFER",
  MMAccountReturn:   "MM_ACCOUNT_RETURN",

  // ops / reconciliation (backoffice 0013_seed_ops_rbac)
  OpsView:    "OPS_VIEW",
  OpsResolve: "OPS_RESOLVE",
  OpsTrigger: "OPS_TRIGGER",
  OpsConfig:  "OPS_CONFIG",

  // ranking / leagues (ranking-service via backoffice proxy)
  RankingView:   "RANKING_VIEW",
  RankingConfig: "RANKING_CONFIG",

  // crypto wallet rail (SM-211 — backoffice 0021_seed_crypto_wallet_rbac)
  CryptoWalletView:     "CRYPTO_WALLET_VIEW",
  CryptoWalletConfig:   "CRYPTO_WALLET_CONFIG",
  CryptoWalletManualOp: "CRYPTO_WALLET_MANUAL_OP",
  // SM-279 treasury refills (backoffice 0022_seed_crypto_refill_rbac)
  CryptoWalletRefillRequest: "CRYPTO_WALLET_REFILL_REQUEST",
  CryptoWalletRefillApprove: "CRYPTO_WALLET_REFILL_APPROVE",

  // AI / MCP (backoffice 0020_seed_ai_rbac)
  AIConnect:          "AI_CONNECT",
  AIConnectionView:   "AI_CONNECTION_VIEW",
  AIConnectionRevoke: "AI_CONNECTION_REVOKE",
  AIUsageView:        "AI_USAGE_VIEW",
  AIActionApprove:    "AI_ACTION_APPROVE",
  AIAutomationManage: "AI_AUTOMATION_MANAGE",

  // broker organizations, staff and hierarchy (SM-621 phase 02 — backoffice
  // 0033_seed_broker_rbac, category "brokers"). Constants only until the
  // 08a screens. BrokerPause gates pause AND resume; BrokerClose gates
  // begin-close AND close. Grants are a proposal pending SM-684: SUPER_ADMIN
  // all; FINANCE view/approve/pause/close/audit; SUPPORT and READONLY view.
  // BrokerAuditView ends in _VIEW but is NOT a read-only-role code.
  BrokerView:           "BROKER_VIEW",
  BrokerCreate:         "BROKER_CREATE",
  BrokerApprove:        "BROKER_APPROVE",
  BrokerPause:          "BROKER_PAUSE",
  BrokerClose:          "BROKER_CLOSE",
  BrokerMove:           "BROKER_MOVE",
  BrokerSettingsUpdate: "BROKER_SETTINGS_UPDATE",
  BrokerStaffManage:    "BROKER_STAFF_MANAGE",
  BrokerAuditView:      "BROKER_AUDIT_VIEW",
  // broker funding (SM-621 phase 04, SM-697 — backoffice
  // 0034_seed_broker_funding_rbac, category "brokers"): pool transfers, a
  // root master's top-up/payout, server credentials, the funding recon run.
  // SUPER_ADMIN + FINANCE. Held ON TOP of the route's own grant (a master
  // top-up is SystemAccountDeposit + CryptoWalletView + this); funding
  // reads stay on BrokerView.
  BrokerFundingOperate: "BROKER_FUNDING_OPERATE",
  // broker exposure, collateral and receivables (SM-621 phase 06b, SM-771 —
  // backoffice 0035_seed_broker_exposure_rbac, category "brokers").
  // BrokerExposureView reads exposure, limits and the receivable list
  // (SUPER_ADMIN, FINANCE and READONLY — not SUPPORT). BrokerExposureConfigure
  // changes a node's limit terms and decides allowance requests;
  // BrokerReceivableManage acknowledges, settles and writes off receivables
  // and decides their pending actions. Both are maker-checker server-side:
  // broker-service refuses an approval by the admin who asked.
  BrokerExposureView:      "BROKER_EXPOSURE_VIEW",
  BrokerExposureConfigure: "BROKER_EXPOSURE_CONFIGURE",
  BrokerReceivableManage:  "BROKER_RECEIVABLE_MANAGE",
  // broker revenue, share allocation and payout (SM-621 phase 07a,
  // SM-710 / SM-713 — backoffice 0036_seed_broker_revenue_rbac, category
  // "brokers", SM-708 §8). SUPER_ADMIN + FINANCE; READONLY holds
  // BrokerRevenueView. BrokerRevenueView reads earnings, recon runs,
  // releases, adjustments, share plans, batches and statements.
  // BrokerRevenueShareManage publishes share plans; BrokerRevenueAdjust
  // adjusts / holds / reassigns and runs recon + release;
  // BrokerPayoutReview prepares batches by hand and approves / rejects /
  // cancels / executes / completes / fails them. There are no payout
  // destinations (SM-878 / SM-882: backoffice 0044 deletes
  // BROKER_PAYOUT_DESTINATION_VERIFY). A batch is not maker-checker since
  // SM-914: its preparer may approve it and one approval is enough, but
  // the preparer cannot reject it and nobody decides twice
  // (BROKER_APPROVAL_INVALID). Revenue adjustments stay maker-checker.
  BrokerRevenueView:        "BROKER_REVENUE_VIEW",
  BrokerRevenueShareManage: "BROKER_REVENUE_SHARE_MANAGE",
  BrokerRevenueAdjust:      "BROKER_REVENUE_ADJUST",
  BrokerPayoutReview:       "BROKER_PAYOUT_REVIEW",
  // broker clearing account (SM-621 phase 07b, SM-780 / SM-782 — backoffice
  // 0037_seed_broker_clearing_rbac, category "brokers"). Reads a node's
  // signed clearing balance and its direction, and the clearing account
  // list. SUPER_ADMIN, FINANCE, SUPPORT and READONLY, every grant by name.
  // There is no operate code: only settlement legs move a clearing balance.
  BrokerClearingView: "BROKER_CLEARING_VIEW",
  // broker investigations (SM-621 phase 08a, SM-716 — backoffice
  // 0038_seed_broker_investigate_rbac, category "brokers"). Reads the
  // composed trace from a user, broker account, order, trade or payout
  // batch through orders, fills, fees, earnings, transfers and payouts.
  // Read-only; SUPER_ADMIN, FINANCE, SUPPORT and READONLY, by name.
  BrokerInvestigate: "BROKER_INVESTIGATE",
  // broker settlement position and out-of-band settlement notes (SM-621
  // phase 09a, SM-796 / SM-798 / SM-799 — backoffice
  // 0039_seed_broker_settlement_rbac, category "brokers"). SUPER_ADMIN and
  // FINANCE all four; SUPPORT and READONLY the view, every grant by name.
  // A note is a memo of an external bank transfer: it moves no money, and
  // its own author may confirm it (PO, SM-914).
  BrokerSettlementView:        "BROKER_SETTLEMENT_VIEW",
  BrokerSettlementExport:      "BROKER_SETTLEMENT_EXPORT",
  BrokerSettlementNoteRecord:  "BROKER_SETTLEMENT_NOTE_RECORD",
  BrokerSettlementNoteConfirm: "BROKER_SETTLEMENT_NOTE_CONFIRM",
  // broker external-credit provider configuration (SM-621 phase 11, SM-634
  // — backoffice 0040_seed_broker_provider_rbac, category "brokers").
  // SUPER_ADMIN and FINANCE both; SUPPORT and READONLY the view, every
  // grant by name. VIEW reads the (redacted) configuration, its enablement
  // readiness and its events; CONFIGURE saves it, enables / disables it
  // and runs the credential rotation drill. The credential is a Secret
  // Manager reference: nothing ever shows a secret value, and only
  // BROKER_PROVIDER_SECRET_WRITE below takes one.
  BrokerProviderView:      "BROKER_PROVIDER_VIEW",
  BrokerProviderConfigure: "BROKER_PROVIDER_CONFIGURE",
  // SM-919, SUPER_ADMIN only: paste a provider's secret key in the Edit
  // configuration dialog. backoffice-service proxies it to broker-service,
  // which stores it in Secret Manager and answers a pinned reference; the
  // console never shows, keeps or logs the value.
  BrokerProviderSecretWrite: "BROKER_PROVIDER_SECRET_WRITE",
  // broker provider launch audit (SM-621 phase 12, SM-643 — backoffice
  // 0041_seed_broker_launch_audit_rbac, category "brokers"). SUPER_ADMIN,
  // FINANCE, SUPPORT and READONLY — the BROKER_PROVIDER_VIEW holders —
  // every grant by name. Read-only: a broker's launch decisions, never a
  // launch code, a provider user id or a personal value.
  BrokerLaunchAuditView: "BROKER_LAUNCH_AUDIT_VIEW",
  // provider-operation recovery cases (SM-621 phase 14, SM-661 — backoffice
  // 0042_seed_broker_provider_recovery_rbac, category "brokers").
  // SUPER_ADMIN and FINANCE both; SUPPORT and READONLY the view, every grant
  // by name. CASE_VIEW reads the case queue and one case's evidence and
  // action history; RECOVERY_ACT takes the six audited actions. An action
  // carries a note only — never an amount, a balance or a transaction id —
  // and broker-service re-checks its eligibility.
  BrokerProviderCaseView:    "BROKER_PROVIDER_CASE_VIEW",
  BrokerProviderRecoveryAct: "BROKER_PROVIDER_RECOVERY_ACT",
  // broker fee plans (SM-835 / SM-838 — backoffice 0043_seed_broker_fee_rbac,
  // category "brokers"). BrokerFeeView reads a root's fee plans, versions and
  // assignments, any node's effective fee schedule and an account's fee
  // resolution (SUPER_ADMIN, FINANCE, SUPPORT and READONLY); BrokerFeeManage
  // creates plans, publishes versions, retires plans and publishes
  // assignments (SUPER_ADMIN and FINANCE), every grant by name. No
  // maker-checker: every change writes one broker audit row. backoffice is
  // the only gate on this path — broker-service enforces the fee rules
  // (ownership, immutability, the GOLD redemption ceiling, the root's
  // FEE_CHANGE status), never the permission. Revenue-share plans stay on
  // BrokerRevenueView / BrokerRevenueShareManage above.
  BrokerFeeView:   "BROKER_FEE_VIEW",
  BrokerFeeManage: "BROKER_FEE_MANAGE",
} as const;
export type PermCode = (typeof Perm)[keyof typeof Perm];

// PermsView is the resolved set of "what can I do?" plus a couple of
// helpers. SUPER_ADMIN bypasses code-level checks (mirrors what the
// seed migration grants it on the server side).
export interface PermsView {
  loaded: boolean;
  /**
   * The acting admin's own id. Lets a page tell "this record is me"
   * apart from "this record is someone else" without a second fetch —
   * SM-535 warns an operator resetting their OWN password that they
   * are about to sign themselves out. Empty until loaded.
   */
  adminId: string;
  roles: string[];
  permissions: string[];
  isSuperAdmin: boolean;
  has: (code: PermCode | string) => boolean;
  hasAny: (...codes: (PermCode | string)[]) => boolean;
  inRole: (role: RoleName | string) => boolean;
}

export function emptyPermsView(): PermsView {
  return {
    loaded: false,
    adminId: "",
    roles: [],
    permissions: [],
    isSuperAdmin: false,
    has: () => false,
    hasAny: () => false,
    inRole: () => false,
  };
}
